import { tuning } from '@/config/tuning';
import { clamp, damp } from '@/math/angles';
import { forwardX, forwardZ, rightX, rightZ } from '@/math/orientation';

/**
 * arcadeCar.ts — THE pure arcade car physics step. No three.js, no DOM, no track:
 * just numbers in, numbers out, so the whole driving feel is unit-testable and
 * deterministic. The entity (Car.ts) layers ground-follow + wall handling on top.
 *
 * This is a velocity-vector + circle-based steering model (which felt far better
 * than a naive `heading += steer * rate`):
 *  - velocity is a real world vector, so the car carries momentum and can slide;
 *  - turning uses ω = steerAngle · speed / radius, with the radius GROWING with
 *    speed (so it never gets twitchy fast) and a HARD cap on ω so steering can
 *    never spin you out;
 *  - high lateral grip keeps it planted; grip drops while boosting / trail-braking
 *    so it drifts;
 *  - steering inverts correctly when reversing.
 *
 * Steering sign convention (WORLD): +steer increases heading (turns toward +X).
 * The player's screen-relative flip (§6.2) is applied OUTSIDE this function; the
 * AI feeds world-space steer here unflipped.
 */

export interface CarPhysicsState {
  x: number;
  z: number;
  heading: number;
  vx: number; // world-plane velocity
  vz: number;
  steerAngle: number; // smoothed steering, -1..1
}

export interface CarInput {
  throttle: number; // -1 (brake/reverse) .. +1 (accelerate)
  steer: number; // -1 .. +1 (world meaning: + increases heading)
  boosting: boolean;
}

export type CarParams = typeof tuning.car;

export function createCarState(x: number, z: number, heading: number): CarPhysicsState {
  return { x, z, heading, vx: 0, vz: 0, steerAngle: 0 };
}

/** Advance the car one fixed step in free space. Mutates `s` in place. */
export function arcadeCarStep(s: CarPhysicsState, input: CarInput, p: CarParams, dt: number): void {
  // 1) Smooth steering toward the input — returns to centre faster than it engages.
  const target = clamp(input.steer, -1, 1);
  const returning = Math.abs(target) < Math.abs(s.steerAngle);
  s.steerAngle = damp(s.steerAngle, target, returning ? p.steerReturn : p.steerSmooth, dt);

  // 2) Decompose world velocity into forward (vf) and lateral (vr) vs. heading.
  const fx = forwardX(s.heading);
  const fz = forwardZ(s.heading);
  const rx = rightX(s.heading);
  const rz = rightZ(s.heading);
  let vf = s.vx * fx + s.vz * fz;
  let vr = s.vx * rx + s.vz * rz;

  // 3) Longitudinal accel with a speed taper (strong off the line, gentle near top).
  const topF = input.boosting ? p.boostTop : p.topSpeed;
  const accelRate = input.boosting ? p.boostAccel : p.accel;
  if (input.throttle > 0) {
    const taper = clamp(1 - vf / topF, 0, 1);
    vf += input.throttle * accelRate * taper * dt;
  } else if (input.throttle < 0) {
    // M92: once braked down to a crawl (reverseEngage; 0 for the AI), COMMIT to
    // reverse rather than demanding exactly 0 forward speed — the exact-zero gate
    // could be held open forever by a persistent contact nudge (see tuning.car).
    if (vf > p.reverseEngage) {
      // Braking — don't snap straight into reverse in one step.
      vf = Math.max(0, vf + input.throttle * p.brake * dt);
    } else {
      const taper = clamp(1 - -vf / p.reverseTop, 0, 1);
      vf += input.throttle * p.reverseAccel * taper * dt;
    }
  }

  // 4) Drag: strong while coasting, light under power. Then clamp so we never
  //    exceed the (boosted or normal) top speed.
  const drag = Math.abs(input.throttle) > 0.05 ? p.dragPower : p.dragCoast;
  vf -= vf * drag * dt;
  vf = clamp(vf, -p.reverseTop, topF);

  // 5) Lateral grip: planted normally; slides while boosting or trail-braking.
  const trailBraking = input.throttle < 0 && vf > 0 && Math.abs(s.steerAngle) > 0.3;
  const grip = input.boosting || trailBraking ? p.driftGrip : p.lateralGrip;
  vr -= vr * Math.min(grip * dt, 1); // clamp factor so high grip can't overshoot past 0

  // 6) Recombine into world velocity.
  s.vx = fx * vf + rx * vr;
  s.vz = fz * vf + rz * vr;

  // 7) Steering -> yaw. Circle-based, speed-sensitive, hard-capped, reverse-aware.
  const speed = Math.hypot(s.vx, s.vz);
  const turnRadius = p.baseTurnRadius + p.turnRadiusGainPerSpeed * speed;
  const turnSpeed = Math.max(speed, p.lowSpeedTurnFloor); // still steerable at a crawl
  const reverseSign = vf < 0 ? -1 : 1;
  let omega = s.steerAngle * (turnSpeed / turnRadius) * reverseSign;
  omega = clamp(omega, -p.maxYaw, p.maxYaw); // can never spin out from steering
  s.heading += omega * dt;

  // 8) Integrate position.
  s.x += s.vx * dt;
  s.z += s.vz * dt;
}
